import { Component, Inject, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material';
import {
  BlogTagCreateRequest,
  BlogTagEditData,
  BlogTagFormDialogData
} from '@app/model/blog-management.model';
import { BlogManagementDataService } from '@app/services/blog-management-data.service';
import { ToastrService } from 'ngx-toastr';
import { finalize, take } from 'rxjs/operators';

@Component({
  selector: 'meu-blog-tag-form',
  templateUrl: './blog-tag-form.component.html',
  styleUrls: ['./blog-tag-form.component.scss']
})
export class BlogTagFormComponent implements OnInit {
  languages = [
    {
      code: 'vi',
      label: 'Tiếng Việt'
    },
    {
      code: 'en',
      label: 'English'
    },
    {
      code: 'zh',
      label: '中文'
    }
  ];

  selectedLanguageIndex = 0;

  isSaving = false;
  isLoadingData = false;

  languageForms: { [languageCode: string]: FormGroup } = {
    vi: this.createLanguageForm(),
    en: this.createLanguageForm(),
    zh: this.createLanguageForm()
  };

  constructor(
    @Inject(MAT_DIALOG_DATA)
    public data: BlogTagFormDialogData,
    private dialogRef: MatDialogRef<BlogTagFormComponent>,
    private blogManagementDataService: BlogManagementDataService,
    private toastr: ToastrService
  ) {}

  ngOnInit(): void {
    if (this.data.mode === 'edit' && this.data.tag) {
      this.loadTagForEdit();
    }
  }

  get modalTitle(): string {
    return this.data.mode === 'edit' ? 'Sửa thẻ' : 'Thêm thẻ';
  }

  get modalDescription(): string {
    return this.data.mode === 'edit'
      ? 'Cập nhật thông tin thẻ Blog'
      : 'Tạo thẻ mới để phân loại bài viết Blog';
  }

  get submitLabel(): string {
    if (this.isLoadingData) {
      return 'Đang tải...';
    }

    if (this.isSaving) {
      return 'Đang lưu...';
    }

    return this.data.mode === 'edit' ? 'Lưu thay đổi' : 'Tạo thẻ';
  }

  get selectedLanguageCode(): string {
    return this.languages[this.selectedLanguageIndex].code;
  }

  get selectedLanguageForm(): FormGroup {
    return this.languageForms[this.selectedLanguageCode];
  }

  closeModal(): void {
    if (this.isSaving || this.isLoadingData) {
      return;
    }

    this.dialogRef.close();
  }

  saveTag(): void {
    if (this.isSaving || this.isLoadingData) {
      return;
    }

    if (this.hasInvalidForm()) {
      this.markAllFormsAsTouched();
      this.toastr.error('Vui lòng nhập đầy đủ thông tin thẻ');

      return;
    }

    const englishValue = this.languageForms.en.value;
    const vietnameseValue = this.languageForms.vi.value;
    const chineseValue = this.languageForms.zh.value;

    const payload: BlogTagCreateRequest = {
      name: (englishValue.name || '').trim(),
      slug: this.normalizeSlug(englishValue.slug),
      translations: [
        {
          language_code: 'vi',
          name: (vietnameseValue.name || '').trim(),
          slug: this.normalizeSlug(vietnameseValue.slug)
        },
        {
          language_code: 'zh',
          name: (chineseValue.name || '').trim(),
          slug: this.normalizeSlug(chineseValue.slug)
        }
      ]
    };

    if (this.hasDuplicateName(payload)) {
      this.toastr.error('Tên thẻ đã tồn tại');
      return;
    }

    if (this.hasDuplicateSlug(payload)) {
      this.toastr.error('Slug của thẻ đã tồn tại');
      return;
    }

    this.submitTag(payload);
  }

  private createLanguageForm(): FormGroup {
    return new FormGroup({
      name: new FormControl('', [Validators.required]),
      slug: new FormControl('', [
        Validators.required,
        Validators.pattern(/^[a-z0-9]+(?:-[a-z0-9]+)*$/)
      ])
    });
  }

  private loadTagForEdit(): void {
    this.isLoadingData = true;

    this.blogManagementDataService
      .getTagForEdit(this.data.tag.id)
      .pipe(
        take(1),
        finalize(() => {
          this.isLoadingData = false;
        })
      )
      .subscribe(
        (tagData) => {
          this.patchTagData(tagData);
        },
        (error) => {
          console.error('Load blog tag failed:', error);
          this.toastr.error('Không thể tải thông tin thẻ');
          this.dialogRef.close();
        }
      );
  }

  private patchTagData(tagData: BlogTagEditData): void {
    this.languageForms.vi.patchValue(tagData.languageContents.vi);
    this.languageForms.en.patchValue(tagData.languageContents.en);
    this.languageForms.zh.patchValue(tagData.languageContents.zh);
  }

  private hasInvalidForm(): boolean {
    return (
      this.languageForms.vi.invalid ||
      this.languageForms.en.invalid ||
      this.languageForms.zh.invalid
    );
  }

  private markAllFormsAsTouched(): void {
    this.languageForms.vi.markAllAsTouched();
    this.languageForms.en.markAllAsTouched();
    this.languageForms.zh.markAllAsTouched();
  }

  private hasDuplicateName(payload: BlogTagCreateRequest): boolean {
    const names = [
      payload.name,
      ...payload.translations.map((translation) => translation.name)
    ].map((name) => name.trim().toLowerCase());

    return this.data.tags.some((tag) => {
      if (this.data.tag && tag.id === this.data.tag.id) {
        return false;
      }

      const tagNames = [
        tag.name,
        ...(tag.translations || []).map((translation) => translation.name)
      ].map((name) => (name || '').trim().toLowerCase());

      return names.some((name) => tagNames.includes(name));
    });
  }

  private hasDuplicateSlug(payload: BlogTagCreateRequest): boolean {
    const slugs = [
      payload.slug,
      ...payload.translations.map((translation) => translation.slug)
    ].map((slug) => slug.trim().toLowerCase());

    return this.data.tags.some((tag) => {
      if (this.data.tag && tag.id === this.data.tag.id) {
        return false;
      }

      const tagSlugs = [
        tag.slug,
        ...(tag.translations || []).map((translation) => translation.slug)
      ].map((slug) => (slug || '').trim().toLowerCase());

      return slugs.some((slug) => tagSlugs.includes(slug));
    });
  }

  private normalizeSlug(slug: string): string {
    return (slug || '').trim().toLowerCase();
  }

  private submitTag(payload: BlogTagCreateRequest): void {
    this.isSaving = true;

    const request$ =
      this.data.mode === 'edit' && this.data.tag
        ? this.blogManagementDataService.updateTag(
            this.data.tag.id,
            payload
          )
        : this.blogManagementDataService.createTag(payload);

    request$
      .pipe(
        take(1),
        finalize(() => {
          this.isSaving = false;
        })
      )
      .subscribe(
        (response) => {
          if (!response || !response.success) {
            this.toastr.error(
              response && response.message
                ? response.message
                : 'Không thể lưu thẻ'
            );

            return;
          }

          this.toastr.success(
            this.data.mode === 'edit'
              ? 'Cập nhật thẻ thành công'
              : 'Tạo thẻ thành công'
          );

          this.dialogRef.close({
            saved: true
          });
        },
        (error) => {
          console.error('Save blog tag failed:', error);

          const errorResponse =
            error && error.error ? error.error : null;

          let message = 'Không thể lưu thẻ';

          if (errorResponse && errorResponse.message) {
            message = errorResponse.message;
          } else if (errorResponse && errorResponse.title) {
            message = errorResponse.title;
          }

          this.toastr.error(message);
        }
      );
  }
}
